<template>
  <div>
    <form>
      <div class="form-group">
        <label for="username">Username</label>
        <input type="text" class="form-control form-control-lg" v-on:keyup="validUsername(user.username)" @input="checkUsernameAvailability()" v-model="user.username" placeholder="Enter username" id="username" required />
        <div class="regex-req">
          <div class="req-title">Username requirements</div>
          <ul class="req-list">
            <li>
              <span
                v-if="usernameReq.showErrors"
                :class="{
                  success: usernameReq.min8char,
                  error: !usernameReq.min8char
                }"
                aria-hidden="true"
              ></span>
              <span v-else class="initial-state" aria-hidden="true"></span>
              Must be a minimum of 8 characters
            </li>
            <li>
              <span
                v-if="usernameReq.showErrors"
                :class="{
                  success: usernameReq.startEndSpecialChar,
                  error: !usernameReq.startEndSpecialChar
                }"
                aria-hidden="true"
              ></span>
              <span v-else class="initial-state" aria-hidden="true"></span>
              Must begin and end with a letter or number
            </li>
            <li>
              <span v-if="checkingUsername" class="spinner-border spinner-border-sm"></span>
              <span
                v-else-if="usernameReq.showErrors"
                :class="{
                  success: usernameReq.unique,
                  error: !usernameReq.unique
                }"
                aria-hidden="true"
              ></span>
              <span v-else class="initial-state" aria-hidden="true"></span>
              Must be unique
            </li>
            <li>
              <span
                v-if="usernameReq.showErrors"
                :class="{
                  success: usernameReq.hasAllowedChar,
                  error: !usernameReq.hasAllowedChar
                }"
                aria-hidden="true"
              ></span>
              <span v-else class="initial-state" aria-hidden="true"></span>
              May use a combination of letters, numbers, and special characters (.-_@+)
            </li>
          </ul>
        </div>
      </div>
      <div class="form-group">
        <label for="password">Password</label>
        <input type="password" class="form-control form-control-lg" @input="validPassword(user.password), validateConfirmPassword()" v-model="user.password" placeholder="Enter password" id="password" required />
      </div>
      <div class="form-group">
        <label for="confirm-password">Confirm Password</label>
        <input type="password" class="form-control form-control-lg" @input="validateConfirmPassword()" v-model="user.confirmPassword" placeholder="Re-enter password" id="confirm-password" required />
        <p class="text-danger" v-if="passwordNotMatched">
          Passwords do not match. Please try again.
        </p>

        <div class="regex-req">
          <div class="req-title">Password requirements:</div>
          <ul class="validation-list">
            <li>
              <span
                v-if="passwordReq.showErrors"
                :class="{
                  success: passwordReq.min8char,
                  error: !passwordReq.min8char
                }"
                aria-hidden="true"
              ></span>
              <span v-else class="initial-state" aria-hidden="true"></span>
              Min. 8 characters
            </li>
            <li>
              <span
                v-if="passwordReq.showErrors"
                :class="{
                  success: passwordReq.anyLetter,
                  error: !passwordReq.anyLetter
                }"
                aria-hidden="true"
              ></span>
              <span v-else class="initial-state" aria-hidden="true"></span>
              at least 1 letter
            </li>
            <li>
              <span
                v-if="passwordReq.showErrors"
                :class="{
                  success: passwordReq.number,
                  error: !passwordReq.number
                }"
                aria-hidden="true"
              ></span>
              <span v-else class="initial-state" aria-hidden="true"></span>
              At least 1 number (12345)
            </li>
            <li>
              <span
                v-if="passwordReq.showErrors"
                :class="{
                  success: passwordReq.specialChar,
                  error: !passwordReq.specialChar
                }"
                aria-hidden="true"
              ></span>
              <span v-else class="initial-state" aria-hidden="true"></span>
              At least 1 symbol (!@#$%&)
            </li>
          </ul>
        </div>
      </div>
      <div class="form-group mb-0">
        <button type="button" class="btn btn-primary btn-block" v-on:click.prevent="completeRegistration" :disabled="!enableSubmitBtn">
          Submit
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import _ from "lodash";

export default {
  name: "CreateUsernamePassword",
  data() {
    return {
      passwordNotMatched: false,
      user: {
        username: "",
        password: "",
        confirmPassword: ""
      },
      usernameReq: {
        min8char: false,
        startEndSpecialChar: false,
        unique: false,
        hasAllowedChar: false,
        showErrors: false
      },
      passwordReq: {
        min8char: false,
        anyLetter: false,
        number: false,
        specialChar: false,
        showErrors: false,
        passwordMatched: false
      },
      checkingUsername: false,
      apiCallInProgress: false
    };
  },
  computed: {
    enableSubmitBtn() {
      if (this.usernameReq.min8char && this.usernameReq.startEndSpecialChar && this.usernameReq.unique && this.usernameReq.hasAllowedChar && this.passwordReq.min8char && this.passwordReq.anyLetter && this.passwordReq.number && this.passwordReq.specialChar && this.passwordReq.passwordMatched && !this.apiCallInProgress) {
        return true;
      } else {
        return false;
      }
    }
  },
  mounted() {
    this.userLogout();
  },
  methods: {
    ...mapActions(["userLogout"]),
    completeRegistration() {
      this.apiCallInProgress = true;
      this.$api
        .registerUser({
          token: this.$route.params.token,
          username: this.user.username,
          password: this.user.password
        })
        .then(
          response => {
            this.apiCallInProgress = false;
            response.body.message;
            this.$router.push({
              name: "login"
            });
          },
          error => {
            this.apiCallInProgress = false;
            this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while completing your registration.");
          }
        );
    },
    validateConfirmPassword() {
      this.passwordNotMatched = false;
      this.passwordReq.passwordMatched = false;
      if (
        this.user.password === this.user.confirmPassword &&
        this.passwordReq.min8char &&
        /* this.passwordReq.lowercase &&
                this.passwordReq.uppercase && */
        this.passwordReq.anyLetter &&
        this.passwordReq.number &&
        this.passwordReq.specialChar
      ) {
        this.passwordNotMatched = false;
        this.passwordReq.passwordMatched = true;
      } else if (this.user.confirmPassword === "") {
        this.passwordNotMatched = false;
      } else if (this.user.password === this.user.confirmPassword) {
        this.passwordNotMatched = false;
        this.passwordReq.passwordMatched = true;
      } else {
        this.passwordNotMatched = true;
      }
    },
    validPassword(password) {
      this.passwordReq.showErrors = true;
      const numberPattern = new RegExp("^(?=.*[0-9]).+$");
      const specialPattern = new RegExp("^(?=.*[!\"@#$%&'(\)*+,-./:;<=>?\[\\\]^_{|}~]).+$");
      const letterPattern = new RegExp("^(?=.*[a-zA-Z]).+$");

      // empty password check
      if (password === "") {
        this.passwordReq.showErrors = false;
      } else if (password.length >= 8) {
        this.passwordReq.min8char = true;
      } else if (password.length < 8) {
        this.passwordReq.min8char = false;
      }

      // any letter pattern check
      if (letterPattern.test(password)) {
        this.passwordReq.anyLetter = true;
      } else {
        this.passwordReq.anyLetter = false;
      }

      //Atleast 1 number
      if (numberPattern.test(password)) {
        this.passwordReq.number = true;
      } else {
        this.passwordReq.number = false;
      }

      //Atleast 1 special char
      if (specialPattern.test(password)) {
        this.passwordReq.specialChar = true;
      } else {
        this.passwordReq.specialChar = false;
      }
    },
    validUsername(username) {
      this.usernameReq.showErrors = true;
      //this.usernameReq.unique = false;
      if (username.trim() == "") {
        //Do not show any errors
        this.usernameReq.showErrors = false;
      } else if (username.length < 8) {
        this.usernameReq.min8char = false;
      } else if (username.length >= 8) {
        this.usernameReq.min8char = true;
      }

      //No special char at the start and end
      const specialCharStartEndPattern = new RegExp("^[a-zA-Z0-9](.*[a-zA-Z0-9])?$");
      if (specialCharStartEndPattern.test(username)) {
        this.usernameReq.startEndSpecialChar = true;
      } else {
        this.usernameReq.startEndSpecialChar = false;
      }

      //Must contain only allowed chars
      const allowedCharPattern = new RegExp("^[a-zA-Z0-9._@+-]*$");
      if (allowedCharPattern.test(username)) {
        this.usernameReq.hasAllowedChar = true;
      } else {
        this.usernameReq.hasAllowedChar = false;
      }
    },
    checkUsernameAvailability: _.debounce(function() {
      if (this.usernameReq.min8char && this.usernameReq.startEndSpecialChar && this.usernameReq.hasAllowedChar) {
        this.checkingUsername = true;
        this.usernameReq.unique = false;
        this.apiCallInProgress = true;
        this.$api
          .checkUsernameAvailability({
            username: this.user.username
          })
          .then(
            response => {
              this.apiCallInProgress = false;
              this.checkingUsername = false;
              if (response.body.errorMessage && response.body.errorMessage == "USERNAME_EXISTS") {
                this.usernameReq.unique = false;
              } else {
                this.usernameReq.unique = true;
              }
            },
            error => {
              this.apiCallInProgress = false;
              this.checkingUsername = false;
              this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while checking username availability.");
            }
          );
      }
    }, 1000)
  }
};
</script>
